// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  field?: string;
  details?: string;
}

// User types
export interface User {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: UserRole;
  isActive: boolean;
  hasCompletedOnboarding?: boolean;
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  OWNER = 'OWNER',
  MANAGER = 'MANAGER',
  VETERINARIAN = 'VETERINARIAN',
  STAFF = 'STAFF',
  CUSTOMER = 'CUSTOMER',
  GUEST = 'GUEST'
}

// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  phone?: string;
  password: string;
  role: UserRole;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

// Patient types
export interface Patient {
  id: string;
  name: string;
  species: string;
  breed: string;
  gender: 'MALE' | 'FEMALE';
  birthDate?: string;
  color?: string;
  microchipId?: string;
  photoUrl?: string;
  ownerId: string;
  clinicId: string;
  createdAt: string;
  updatedAt: string;
  owner?: User;
  clinic?: Clinic;
}

// Clinic types
export interface Clinic {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  logoUrl?: string;
  subscriptionStatus: SubscriptionStatus;
  createdAt: string;
  updatedAt: string;
}

export enum SubscriptionStatus {
  TRIAL = 'TRIAL',
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  SUSPENDED = 'SUSPENDED'
}

// Medical Record types
export interface MedicalRecord {
  id: string;
  patientId: string;
  veterinarianId: string;
  clinicId: string;
  visitDate: string;
  chiefComplaint: string;
  diagnosis?: string;
  treatment?: string;
  prescription?: string;
  notes?: string;
  weight?: number;
  temperature?: number;
  status: MedicalRecordStatus;
  createdAt: string;
  updatedAt: string;
  patient?: Patient;
  veterinarian?: User;
  clinic?: Clinic;
}

export enum MedicalRecordStatus {
  OUTPATIENT = 'OUTPATIENT',
  INPATIENT = 'INPATIENT',
  DISCHARGED = 'DISCHARGED',
  REFERRED = 'REFERRED'
}

// Invoice types
export interface Invoice {
  id: string;
  invoiceNumber: string;
  patientId: string;
  ownerId: string;
  clinicId: string;
  issueDate: string;
  dueDate: string;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  status: InvoiceStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  patient?: Patient;
  owner?: User;
  clinic?: Clinic;
  items?: InvoiceItem[];
  payments?: Payment[];
}

export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  PAID = 'PAID',
  PARTIAL = 'PARTIAL',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED'
}

export interface InvoiceItem {
  id: string;
  invoiceId: string;
  productId?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  product?: Product;
}

export interface Payment {
  id: string;
  invoiceId: string;
  amount: number;
  method: PaymentMethod;
  status: PaymentStatus;
  transactionId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export enum PaymentMethod {
  CASH = 'CASH',
  TRANSFER = 'TRANSFER',
  CREDIT_CARD = 'CREDIT_CARD',
  E_WALLET = 'E_WALLET',
  XENDIT = 'XENDIT'
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED'
}

// Product types
export interface Product {
  id: string;
  name: string;
  description?: string;
  category: ProductCategory;
  unit: string;
  price: number;
  stockQuantity: number;
  minStockAlert: number;
  expiryDate?: string;
  sku: string;
  barcode?: string;
  clinicId: string;
  createdAt: string;
  updatedAt: string;
  clinic?: Clinic;
}

export enum ProductCategory {
  MEDICINE = 'MEDICINE',
  FOOD = 'FOOD',
  ACCESSORY = 'ACCESSORY',
  SERVICE = 'SERVICE',
  VACCINE = 'VACCINE',
  CONSUMABLE = 'CONSUMABLE'
}

// Clinic Access types
export interface ClinicAccess {
  id: string;
  userId: string;
  clinicId: string;
  accessRole: ClinicAccessRole;
  createdAt: string;
  updatedAt: string;
  user?: User;
  clinic?: Clinic;
}

export enum ClinicAccessRole {
  OWNER = 'OWNER',
  MANAGER = 'MANAGER',
  VETERINARIAN = 'VETERINARIAN',
  STAFF = 'STAFF',
  VIEWER = 'VIEWER'
}

// Clinic Setup types
export interface FileUploadResponse {
  url: string;
  filename: string;
  originalName: string;
  size: number;
}

export interface ClinicSetupData {
  ktpDocumentUrl: string;
  ktaPdhiDocumentUrl: string;
  clinicName: string;
  businessType: 'pt' | 'cv' | 'perseorangan';
  address: string;
  city: string;
  province: string;
  postalCode: string;
  phone: string;
  email: string;
  facilities?: {
    diagnostic?: string[];
    laboratory?: string[];
    hospitalization?: string[];
    surgery?: string[];
  };
}
